<template>
  <v-skeleton-loader
    v-if="isLoading"
    type="text"
    width="110px"
  />
  <button
    v-else
    type="button"
    class="filters-toggle"
    :class="{
      'use-wide-screen-breakpoint': useWideScreenBreakpoint,
      'bg-bgDark': openDrawer,
      'bg-bgDefault': !openDrawer,
    }"
    :disabled="isLoading || isDisabled"
    :aria-expanded="openDrawer"
    aria-controls="filters-drawer"
    @click="openDrawer = !openDrawer"
  >
    <v-icon icon="$filter-list" :size="24" />
    <span>{{ label }}</span>
    <AppliedFiltersDot
      v-if="areFiltersApplied"
      class="ml-2"
    />
  </button>
</template>

<script setup>
import { computed } from 'vue';
import AppliedFiltersDot from '@/components/filter/AppliedFiltersDot.vue';
import { useAppBase } from '@/stores/app/base';
import { useAppSideNav } from '@/stores/app/appSideNav';
import { storeToRefs } from 'pinia';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  areFiltersApplied: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  useWideScreenBreakpoint: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:model-value']);

const appBaseStore = useAppBase();
const { windowWidth } = storeToRefs(appBaseStore);

const { isSideNavOpen } = storeToRefs(useAppSideNav());
const { updateAppSideNavInLocalStorage } = useAppSideNav();

const openDrawer = computed({
  get: () => props.modelValue,
  set: (areFiltersOpen) => {
    emit('update:model-value', areFiltersOpen);

    if (areFiltersOpen && isSideNavOpen.value) {
      isSideNavOpen.value = false;
    }

    updateAppSideNavInLocalStorage(isSideNavOpen.value);
  },
});

const useDisabledLabel = computed(() => windowWidth.value > 1799 && props.useWideScreenBreakpoint);

const label = computed(() => {
  if (useDisabledLabel.value) return 'Hide filters';

  return openDrawer.value ? 'Hide filters' : 'Show filters';
});
</script>

<style lang="less" scoped>
.filters-toggle {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 1px solid @stroke-default;
  background-color: @white;
  padding: 7px;
  border-radius: 8px;
  gap: 2px;

  &[disabled] {
    pointer-events: none;
    filter: opacity(0.5);
  }

  &:hover {
    cursor: pointer;
  }

  span {
    font-size: 13px;
    font-weight: 500;
  }

  :deep(.v-icon) path {
    fill: rgb(var(--v-theme-iconDefault));
  }
}

.filters-toggle.use-wide-screen-breakpoint {
  @media screen and (min-width: 1800px) {
    pointer-events: none;
    filter: opacity(0.5);
  }
}

:deep(.v-skeleton-loader__text) {
  margin: 12px;
}
</style>
